import { byId, money, parseNumber } from './core';
import {
  classifyHolding,
  computeCashToTarget,
  computeContribution,
  computeRebalance,
  emptyValues,
  sumValues,
  TARGET_CLASS_LABELS,
  TARGET_CLASSES,
  targetsSumToHundred,
} from './rebalance';
import type {
  CashToTargetPlan,
  ClassValues,
  ContributionPlan,
  RebalancePlan,
  TargetClass,
} from './rebalance';

const STORAGE_KEY = 'portfolio-viewer.targets';
const DEFAULT_TARGETS: ClassValues = { gold: 25, stocks: 25, bonds: 25, cash: 25 };

// Semantic colours so a class reads the same everywhere: warm gold, blue
// stocks, green bonds, neutral cash.
const CLASS_COLOURS: Record<TargetClass, string> = {
  gold: '#d97706',
  stocks: '#3b82f6',
  bonds: '#059669',
  cash: '#6b7280',
};

interface Allocation {
  current: ClassValues;
  unclassified: number;
  total: number;
  currency: string;
}

let allocation: Allocation | null = null;
// Whether the user has asked for a plan yet — gates the live re-rendering so a
// plan only appears after an explicit "Show plan", then tracks edits.
let planShown = false;

function loadTargets(): ClassValues {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_TARGETS };
    const parsed = JSON.parse(raw) as Partial<Record<TargetClass, unknown>>;
    const targets = emptyValues();
    for (const cls of TARGET_CLASSES) {
      const value = Number(parsed[cls]);
      targets[cls] = Number.isFinite(value) ? clampPercent(value) : DEFAULT_TARGETS[cls];
    }
    return targets;
  } catch {
    return { ...DEFAULT_TARGETS };
  }
}

function saveTargets(targets: ClassValues): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(targets));
  } catch {
    // Private mode or storage disabled — targets simply won't persist.
  }
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

function readTargets(): ClassValues {
  const targets = emptyValues();
  for (const cls of TARGET_CLASSES) {
    targets[cls] = clampPercent(parseNumber(numberInput(cls).value) ?? 0);
  }
  return targets;
}

function numberInput(cls: TargetClass): HTMLInputElement {
  return byId<HTMLInputElement>(`target-number-${cls}`);
}

function rangeInput(cls: TargetClass): HTMLInputElement {
  return byId<HTMLInputElement>(`target-range-${cls}`);
}

function setTargetValue(cls: TargetClass, value: number): void {
  const rounded = String(Math.round(clampPercent(value)));
  numberInput(cls).value = rounded;
  rangeInput(cls).value = rounded;
}

function buildTargetInputs(targets: ClassValues): void {
  byId<HTMLElement>('targetInputs').innerHTML = TARGET_CLASSES.map((cls) => {
    const value = Math.round(targets[cls]);
    const label = TARGET_CLASS_LABELS[cls];
    return `
      <div class="target-field" style="--swatch:${CLASS_COLOURS[cls]}">
        <div class="target-head"><i class="swatch"></i><span>${label}</span></div>
        <div class="target-input">
          <input id="target-number-${cls}" class="target-number" type="number" inputmode="numeric"
                 min="0" max="100" step="1" value="${value}" aria-label="${label} target percentage">
          <span class="pct">%</span>
        </div>
        <input id="target-range-${cls}" class="target-range" type="range" min="0" max="100" step="1"
               value="${value}" aria-label="${label} target percentage" tabindex="-1">
      </div>`;
  }).join('');
}

function onTargetsChanged(): void {
  const targets = readTargets();
  saveTargets(targets);
  updateTotalsBadge(targets);
  updateButtonState(targets);
  updateCashSuggestion();
  if (planShown) renderPlan();
}

function updateTotalsBadge(targets: ClassValues): void {
  const total = Math.round(sumValues(targets));
  const badge = byId<HTMLElement>('targetsTotal');
  if (targetsSumToHundred(targets)) {
    badge.textContent = 'Total 100% ✓';
    badge.className = 'targets-total ok';
  } else {
    const diff = 100 - total;
    const hint = diff > 0 ? `${diff}% left` : `${Math.abs(diff)}% over`;
    badge.textContent = `Total ${total}% · ${hint}`;
    badge.className = 'targets-total warn';
  }
}

function currentMode(): 'rebalance' | 'contribute' {
  const checked = document.querySelector<HTMLInputElement>('input[name="rebalanceMode"]:checked');
  return checked?.value === 'contribute' ? 'contribute' : 'rebalance';
}

function updateButtonState(targets: ClassValues): void {
  const button = byId<HTMLButtonElement>('rebalanceButton');
  const hint = byId<HTMLElement>('rebalanceHint');
  const mode = currentMode();

  if (!targetsSumToHundred(targets)) {
    button.disabled = true;
    hint.textContent = 'Target weights must add up to 100% to build a plan.';
    return;
  }
  if (!allocation) {
    button.disabled = true;
    hint.textContent = 'Update your portfolio first, then build a rebalancing plan.';
    return;
  }
  if (mode === 'rebalance' && allocation.total <= 0) {
    button.disabled = true;
    hint.textContent = 'None of your holdings are classified as Gold, Stocks, Bonds or Cash yet.';
    return;
  }
  if (mode === 'contribute') {
    const cash = parseNumber(byId<HTMLInputElement>('contributionAmount').value) ?? 0;
    if (cash <= 0) {
      // The suggestion panel (updateCashSuggestion) does the talking here, so
      // keep the hint quiet rather than duplicating the prompt.
      button.disabled = true;
      hint.textContent = '';
      return;
    }
  }
  button.disabled = false;
  hint.textContent = mode === 'contribute'
    ? 'Deploys new cash into the classes that are below target.'
    : 'Sells overweight classes and buys underweight ones — your total invested stays the same.';
}

function fmt(value: number, currency: string): string {
  return money(Math.abs(value) < 0.005 ? 0 : value, currency);
}

function pct(value: number): string {
  return `${value.toFixed(1)}%`;
}

function swatch(cls: TargetClass): string {
  return `<i class="swatch" style="background:${CLASS_COLOURS[cls]}"></i>`;
}

function actionPill(action: 'buy' | 'sell' | 'hold', amount: number, currency: string): string {
  if (action === 'hold') return '<span class="pill">Hold</span>';
  const label = action === 'buy' ? 'Buy' : 'Sell';
  return `<span class="pill ${action}">${label} ${fmt(amount, currency)}</span>`;
}

function renderRebalancePlan(plan: RebalancePlan, currency: string): string {
  const trades = plan.rows.filter((row) => row.action !== 'hold');
  const summary = trades.length === 0
    ? '<p class="rebalance-summary ok-text">Your portfolio already matches the target mix — nothing to trade.</p>'
    : `<p class="rebalance-summary">Sell <strong>${fmt(plan.totalSell, currency)}</strong> and buy <strong>${fmt(plan.totalBuy, currency)}</strong> to reach your target mix. Total invested stays at <strong>${fmt(plan.total, currency)}</strong>.</p>`;

  const rows = plan.rows.map((row) => `
    <tr>
      <td><span class="class-cell">${swatch(row.assetClass)}${TARGET_CLASS_LABELS[row.assetClass]}</span></td>
      <td>${fmt(row.currentValue, currency)}<span class="sub">${pct(row.currentPct)}</span></td>
      <td>${fmt(row.targetValue, currency)}<span class="sub">${pct(row.targetPct)}</span></td>
      <td>${actionPill(row.action, row.amount, currency)}</td>
    </tr>`).join('');

  return `${summary}
    <div class="table-scroll"><table class="plan-table">
      <thead><tr><th>Asset class</th><th>Current</th><th>Target</th><th>Action</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>`;
}

function renderContributionPlan(
  plan: ContributionPlan,
  currency: string,
  cashToTarget: CashToTargetPlan,
): string {
  const overweight = plan.rows.filter((row) => row.overweight);
  let note: string;
  if (plan.needsRebalance) {
    const names = overweight.map((row) => TARGET_CLASS_LABELS[row.assetClass]).join(' and ');
    // If the target is reachable by buying only, tell them the total contribution
    // that would avoid selling entirely — the more appealing alternative.
    const topUp = cashToTarget.reachable && cashToTarget.minCash > plan.cash + 0.005
      ? ` Alternatively, invest <strong>${fmt(cashToTarget.minCash, currency)}</strong> in total (${fmt(cashToTarget.minCash - plan.cash, currency)} more) to reach the target without selling anything.`
      : '';
    note = `<div class="rebalance-note warn">
      <strong>${fmt(plan.cash, currency)} isn't enough to fully reach your target.</strong>
      ${names} ${overweight.length > 1 ? 'are' : 'is'} already above target, so buying alone can't get there.
      Deploy the new cash as below to get closer, then rebalance by selling
      <strong>${fmt(plan.sellNeeded, currency)}</strong> of ${names} to finish.${topUp}</div>`;
  } else {
    note = `<div class="rebalance-note ok">
      <strong>${fmt(plan.deployed, currency)} of new cash</strong> deployed as below puts every class on target
      (new total ${fmt(plan.newTotal, currency)}).</div>`;
  }

  const rows = plan.rows.map((row) => {
    const buyCell = row.overweight
      ? `<span class="pill">Over target</span><span class="sub">sell ${fmt(row.sellToReachTarget, currency)} to fix</span>`
      : row.buy < 0.005
        ? '<span class="pill">—</span>'
        : `<span class="pill buy">Buy ${fmt(row.buy, currency)}</span>`;
    return `
      <tr>
        <td><span class="class-cell">${swatch(row.assetClass)}${TARGET_CLASS_LABELS[row.assetClass]}</span></td>
        <td>${fmt(row.currentValue, currency)}<span class="sub">${pct(row.currentPct)}</span></td>
        <td>${buyCell}</td>
        <td>${fmt(row.projectedValue, currency)}<span class="sub">${pct(row.projectedPct)}</span></td>
        <td>${fmt(row.targetValue, currency)}<span class="sub">${pct(row.targetPct)}</span></td>
      </tr>`;
  }).join('');

  return `${note}
    <div class="table-scroll"><table class="plan-table">
      <thead><tr><th>Asset class</th><th>Current</th><th>Buy with new cash</th><th>Projected</th><th>Target</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>`;
}

function hidePlan(): void {
  const result = byId<HTMLElement>('rebalanceResult');
  result.classList.add('hidden');
  result.innerHTML = '';
}

function renderPlan(): void {
  const result = byId<HTMLElement>('rebalanceResult');
  const targets = readTargets();

  // Any state that can't produce a valid plan clears the result rather than
  // leaving a stale one on screen (e.g. after targets drift off 100%).
  if (!allocation || !targetsSumToHundred(targets)) {
    hidePlan();
    return;
  }

  const currency = allocation.currency;

  if (currentMode() === 'contribute') {
    const cash = parseNumber(byId<HTMLInputElement>('contributionAmount').value) ?? 0;
    if (cash <= 0) {
      hidePlan();
      return;
    }
    const cashToTarget = computeCashToTarget(allocation.current, targets);
    result.innerHTML = renderContributionPlan(
      computeContribution(allocation.current, targets, cash),
      currency,
      cashToTarget,
    );
  } else {
    if (allocation.total <= 0) {
      hidePlan();
      return;
    }
    result.innerHTML = renderRebalancePlan(computeRebalance(allocation.current, targets), currency);
  }
  result.classList.remove('hidden');
}

/**
 * When the user is in "invest new cash" mode but hasn't entered an amount yet,
 * proactively suggest the two ways to hit the target: rebalance now, or add a
 * specific amount of cash and buy only. Hidden in every other state.
 */
function updateCashSuggestion(): void {
  const el = byId<HTMLElement>('cashSuggestion');
  const targets = readTargets();
  const cash = parseNumber(byId<HTMLInputElement>('contributionAmount').value) ?? 0;
  const show = currentMode() === 'contribute'
    && cash <= 0
    && allocation !== null
    && allocation.total > 0
    && targetsSumToHundred(targets);

  if (!show || !allocation) {
    el.classList.add('hidden');
    el.innerHTML = '';
    return;
  }

  const currency = allocation.currency;
  const plan = computeCashToTarget(allocation.current, targets);

  if (plan.reachable && plan.minCash < 0.005) {
    el.innerHTML = 'Your holdings already match the target mix — no new cash or trades needed.';
  } else if (!plan.reachable) {
    const names = plan.blockingClasses.map((cls) => TARGET_CLASS_LABELS[cls]).join(' and ');
    el.innerHTML = `Adding cash alone can't reach this target — you hold ${names} but its target is 0%, so it has to be sold. `
      + '<button class="linklike" type="button" data-action="rebalance">Rebalance instead</button>.';
  } else {
    const suggested = Math.ceil(plan.minCash);
    el.innerHTML = 'No new cash entered yet. To reach your target you can '
      + '<button class="linklike" type="button" data-action="rebalance">rebalance what you hold</button>, '
      + `or invest <button class="linklike" type="button" data-action="use-cash" data-amount="${suggested}">${fmt(suggested, currency)} of new cash</button> and deploy it with no selling.`;
  }

  el.classList.remove('hidden');
  wireSuggestionActions();
}

function wireSuggestionActions(): void {
  byId<HTMLElement>('cashSuggestion')
    .querySelectorAll<HTMLButtonElement>('.linklike')
    .forEach((btn) => btn.addEventListener('click', () => {
      if (btn.dataset.action === 'rebalance') {
        selectMode('rebalance');
      } else if (btn.dataset.action === 'use-cash') {
        byId<HTMLInputElement>('contributionAmount').value = btn.dataset.amount ?? '';
        planShown = true;
        updateButtonState(readTargets());
        updateCashSuggestion();
        renderPlan();
      }
    }));
}

function selectMode(mode: 'rebalance' | 'contribute'): void {
  const radio = document.querySelector<HTMLInputElement>(`input[name="rebalanceMode"][value="${mode}"]`);
  if (radio) radio.checked = true;
  byId<HTMLElement>('contributionField').classList.toggle('hidden', mode !== 'contribute');
  if (mode === 'rebalance') planShown = true;
  updateButtonState(readTargets());
  updateCashSuggestion();
  renderPlan();
}

/**
 * Fed the freshly priced holdings after an analysis so the rebalancing card can
 * light up with the real current mix. Called from portfolio.render().
 */
export function syncRebalanceAllocation(
  priced: ReadonlyArray<{
    resolvedAssetClass: string;
    value: number;
    ISIN?: string;
    Ticker?: string;
    symbol?: string;
  }>,
  currency: string,
): void {
  const current = emptyValues();
  let unclassified = 0;
  for (const row of priced) {
    const cls = classifyHolding(row.resolvedAssetClass, [row.ISIN, row.Ticker, row.symbol]);
    if (cls) current[cls] += row.value;
    else unclassified += row.value;
  }

  allocation = { current, unclassified, total: sumValues(current), currency };
  byId<HTMLElement>('rebalanceSection').classList.remove('hidden');

  const note = byId<HTMLElement>('unclassifiedNote');
  if (unclassified > 0.005) {
    note.textContent = `${money(unclassified, currency)} of holdings aren't classified as Gold, Stocks, Bonds or Cash and are excluded from the plan. Tag them with one of these asset classes in your CSV to include them.`;
    note.classList.remove('hidden');
  } else {
    note.classList.add('hidden');
  }

  updateButtonState(readTargets());
  updateCashSuggestion();
  // Refresh a plan that's already on screen so it reflects the new prices.
  if (planShown) renderPlan();
}

export function initialiseRebalance(): void {
  const targets = loadTargets();
  buildTargetInputs(targets);
  updateTotalsBadge(targets);
  updateButtonState(targets);

  for (const cls of TARGET_CLASSES) {
    numberInput(cls).addEventListener('input', () => {
      const input = numberInput(cls);
      const clamped = clampPercent(parseNumber(input.value) ?? 0);
      // Reflect an out-of-range entry (e.g. 150) back into the field so what the
      // user sees always matches the value the badge, storage and plan use.
      // Leave a mid-edit empty field alone so it can still be cleared to retype.
      if (input.value !== '' && Number(input.value) !== clamped) input.value = String(clamped);
      rangeInput(cls).value = String(clamped);
      onTargetsChanged();
    });
    rangeInput(cls).addEventListener('input', () => {
      numberInput(cls).value = rangeInput(cls).value;
      onTargetsChanged();
    });
  }

  byId<HTMLButtonElement>('targetsEven').addEventListener('click', () => {
    for (const cls of TARGET_CLASSES) setTargetValue(cls, 25);
    onTargetsChanged();
  });

  document.querySelectorAll<HTMLInputElement>('input[name="rebalanceMode"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      byId<HTMLElement>('contributionField').classList.toggle('hidden', currentMode() !== 'contribute');
      updateButtonState(readTargets());
      updateCashSuggestion();
      if (planShown) renderPlan();
    });
  });

  byId<HTMLInputElement>('contributionAmount').addEventListener('input', () => {
    updateButtonState(readTargets());
    updateCashSuggestion();
    if (planShown) renderPlan();
  });

  byId<HTMLButtonElement>('rebalanceButton').addEventListener('click', () => {
    planShown = true;
    renderPlan();
  });

  updateCashSuggestion();
}
